<template>
  <SignatureDialogLayout :title="step === 2 ? 'Dados para faturamento' : 'Cartão de crédito'">
    <template v-if="step === 1">
      <div class="credit-card">
        <div class="input">
          <label for="holderName">Nome do cartão <span>*</span></label>
          <input v-model="creditCard.holderName" type="text" id="holderName" name="holderName" placeholder="Nome Sobrenome" />
        </div>

        <div class="input mt-3">
          <label for="cardNumber">Número do cartão <span>*</span></label>
          <input
            v-model="creditCard.number"
            type="text"
            id="cardNumber"
            name="cardNumber"
            placeholder="0000 0000 0000 0000"
            v-mask="['#### #### #### ####']"
          />
        </div>

        <div class="grid mt-3">
          <div class="input">
            <label for="expiryMonth">Mês<span>*</span></label>
            <input v-model="creditCard.expiryMonth" type="phone" id="expiryMonth" name="expiryMonth" placeholder="00" max="2" v-mask="['##']" />
          </div>
          <div class="input">
            <label for="expiryYear">Ano <span>*</span></label>
            <input v-model="creditCard.expiryYear" type="phone" id="expiryYear" name="expiryYear" placeholder="00" max="2" v-mask="['##']" />
          </div>
          <div class="input">
            <label for="cvv">CVV <span>*</span></label>
            <input v-model="creditCard.ccv" type="phone" id="cvv" name="cvv" placeholder="000" max="3" v-mask="['###']" />
          </div>
        </div>
      </div>

      <SignatureSafePaymentSection />
    </template>

    <template v-if="step === 2">
      <div class="credit-card">
        <div class="input">
          <label for="name">Nome completo <span>*</span></label>
          <input v-model="creditCardHolderInfo.name" key="cardName" type="text" id="name" name="name" placeholder="Nome completo" />
        </div>

        <div class="input">
          <label for="email">E-mail <span>*</span></label>
          <input v-model="creditCardHolderInfo.email" key="email" type="text" id="email" name="email" placeholder="e-mail@dominio.com" />
        </div>

        <div class="input">
          <label for="cpfCnpj">CPF ou CNPJ <span>*</span></label>
          <input
            v-model="creditCardHolderInfo.cpfCnpj"
            type="phone"
            id="cpfCnpj"
            name="cpfCnpj"
            placeholder="00.000.000/0000-00"
            v-mask="['##.###.###/####-##', '###.###.###-##']"
          />
        </div>

        <div class="input">
          <label for="phone">Telefone <span>*</span></label>
          <input
            v-model="creditCardHolderInfo.phone"
            type="phone"
            id="phone"
            name="phone"
            placeholder="(00) 0 0000-0000"
            v-mask="['(##) ####-####', '(##) #####-####']"
          />
        </div>

        <div class="input">
          <label for="address">Endereço <span>*</span></label>
          <input
            v-model="creditCardHolderInfo.addressComplement"
            type="text"
            id="address"
            name="address"
            placeholder="Rua Mato Grosso, Bairro Brasil "
          />
        </div>

        <div class="grid-2 mt-3">
          <div class="input">
            <label for="addressNumber">Número<span>*</span></label>
            <input v-model="creditCardHolderInfo.addressNumber" type="phone" id="addressNumber" name="addressNumber" placeholder="000" />
          </div>
          <div class="input">
            <label for="postalCode">CEP <span>*</span></label>
            <input
              v-model="creditCardHolderInfo.postalCode"
              type="phone"
              id="postalCode"
              name="postalCode"
              placeholder="00000-000"
              v-mask="['#####-###']"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <SignatureResume v-if="step === 1">
        <template #checkTerm>
          <div style="display: flex; align-items: center; background-color: #fff; border-radius: 6px; padding: 6px; margin-bottom: 15px">
            <v-checkbox v-model="acceptTerm" hide-details style="padding: 0 10px;"> </v-checkbox>
            <div style="font-size: 13px">
              <span>Li e aceito os <span style="font-weight: 600" @click="openMembershipTerms">Termos de adesão</span> e </span>
              <span style="font-weight: 600" @click="openTerms">Termos de uso e privacidade.</span>
            </div>
          </div>
        </template>

        <template #actions>
          <VBtn depressed @click="$emit('back')">Voltar</VBtn>
          <VBtn color="primary" :disabled="!acceptTerm" depressed @click="validateCreditCard">Continuar</VBtn>
        </template>
      </SignatureResume>

      <template v-else>
        <VBtn depressed @click="step = 1">Voltar</VBtn>
        <VBtn color="primary" depressed @click="validateCreditCardHolderInfo">Finalizar</VBtn>
      </template>
    </template>

    <VSnackbar v-model="snackbar.isOpen" color="error" :timeout="2500" absolute shaped bottom>
      {{ snackbar.text }}
    </VSnackbar>
  </SignatureDialogLayout>
</template>

<script>
import { z } from 'zod';
import SignatureDialogLayout from '../SignatureDialogLayout';
import SignatureResume from '../SignatureResume';
import SignatureSafePaymentSection from '../SignatureSafePaymentSection';

const preProcessNumber = (numberOfDigits) =>
  z.preprocess((v) => {
    const data = v.replaceAll(' ', '');
    if (data.length !== numberOfDigits) throw new Error(`Must have ${numberOfDigits} digits`);

    return Number(data);
  }, z.number());

const CreditCardSchema = z.object({
  holderName: z.string().min(6),
  number: preProcessNumber(16),
  expiryMonth: preProcessNumber(2),
  expiryYear: preProcessNumber(2),
  ccv: preProcessNumber(3)
});

const CreditCardHolderInfoSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  cpfCnpj: z.string(),
  postalCode: z.string().length(9),
  addressNumber: z.string(),
  addressComplement: z.string(),
  phone: z.string()
});

export default {
  name: 'CreditCardPayment',
  components: { SignatureDialogLayout, SignatureResume, SignatureSafePaymentSection },
  data() {
    return {
      step: 1,
      creditCard: {
        holderName: null,
        number: null,
        expiryMonth: null,
        expiryYear: null,
        ccv: null
      },
      creditCardHolderInfo: {
        name: null,
        email: null,
        cpfCnpj: null,
        postalCode: null,
        addressNumber: null,
        addressComplement: null,
        phone: null,
        mobilePhone: null
      },
      snackbar: {
        isOpen: false,
        text: ''
      },
      acceptTerm: false
    };
  },
  methods: {
    validateCreditCard() {
      try {
        CreditCardSchema.parse(this.creditCard);
        this.step = 2;
      } catch {
        this.snackbar = {
          isOpen: true,
          text: 'Dados incorretos! Confira os dados antes de continuar.'
        };
      }
    },
    validateCreditCardHolderInfo() {
      try {
        CreditCardHolderInfoSchema.parse(this.creditCardHolderInfo);

        this.$emit('save', {
          creditCard: this.creditCard,
          creditCardHolderInfo: this.creditCardHolderInfo
        });
      } catch {
        this.snackbar = {
          isOpen: true,
          text: 'Dados incorretos! Confira os dados antes de continuar.'
        };
      }
    },
    openTerms() {
      this.$store.dispatch('Auth/openTerms');
    },
    openMembershipTerms() {
      this.$store.dispatch('Auth/openMembershipTerms');
    }
  }
};
</script>
<style lang="scss" scoped>
.v-input--selection-controls {
  margin-top: 0px !important;
  padding-top: 0px !important;
}

.input {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;

  label {
    color: #32343a;
    font-size: 12px;
    margin-bottom: 4px;

    span {
      color: #d63928;
    }
  }

  input {
    max-width: 100%;
    border-radius: 6px;
    border: 1px solid #d5d7dd;
    background: #fff;
    padding: 8px 12px;
    font-size: 16px;
    color: #6b7888;

    &::placeholder {
      font-family: inherit;
      color: #8899ad;
      font-size: inherit;
    }
  }
}

.grid {
  display: grid;
  grid-template-columns: calc(33% - 11px) calc(33% - 10px) calc(33% - 11px);
  grid-gap: 16px;
}

.grid-2 {
  display: grid;
  grid-template-columns: calc(50% - 8px) calc(50% - 8px);
  grid-gap: 16px;
}
</style>
